import React, { useState, useEffect, ReactNode } from 'react';
import { Bell, Power, AlertTriangle, CheckCircle, X, ExternalLink, ClipboardCheck } from 'lucide-react';
import { useNotificationStore, NOTIFICATION_STYLES } from '../../stores/notificationStore';
import './Header.css';

interface HeaderProps {
  userMenu?: ReactNode;
  isAdmin?: boolean;
  onToggleAdmin?: () => void;
}

const SYNAPSE_API = '/api/synapse';
const SUPABASE_URL = 'https://symgjyicrpuixzeoxxgl.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN5bWdqeWljcnB1aXh6ZW94eGdsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQ1NTA4NzcsImV4cCI6MjA4MDEyNjg3N30.cO8Xqy8CbAGmYpMKGCqfSb_qMDL_6YDOZ3DLv-2b0LA';

const Header: React.FC<HeaderProps> = ({ userMenu, isAdmin, onToggleAdmin }) => {
  const [health, setHealth] = useState<{ status: string } | null>(null);
  const [workerCount, setWorkerCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);

  // Use centralized notification store
  const { notifications, getRecent, markAsRead, dismiss, dismissAll, unreadCount } = useNotificationStore();

  useEffect(() => {
    let consecutiveFailures = 0;
    const maxBackoff = 60000; // Max 60s between polls when offline

    const fetchData = async () => {
      try {
        const healthRes = await fetch(`${SYNAPSE_API}/health`, { signal: AbortSignal.timeout(3000) });
        if (healthRes.ok) {
          const healthData = await healthRes.json();
          setHealth(healthData);
          consecutiveFailures = 0; // Reset on success

          // Only fetch metrics if health is OK
          const metricsRes = await fetch(`${SYNAPSE_API}/metrics`, { signal: AbortSignal.timeout(3000) });
          if (metricsRes.ok) {
            const metrics = await metricsRes.json();
            setWorkerCount(metrics.active_tasks + metrics.completed_tasks);
            setQueueCount((metrics.pending_tasks || 0) + (metrics.active_tasks || 0));
          }
        } else {
          setHealth({ status: 'offline' });
          consecutiveFailures++;
        }
      } catch {
        // Silent fail - don't spam console
        setHealth({ status: 'offline' });
        consecutiveFailures++;
      }
    };

    fetchData();
    // Use longer interval (15s) to reduce spam
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  // Get addNotification from store for local events
  const addNotification = useNotificationStore.getState().addNotification;

  const handleEmergencyRestart = async () => {
    if (isRestarting) return;

    const confirmed = window.confirm('Restart Synapse pipeline? Active tasks will be paused.');
    if (!confirmed) return;

    setIsRestarting(true);
    addNotification({ type: 'warning', title: 'Synapse Restart', message: 'Restarting pipeline...' });

    try {
      // First pause
      await fetch(`${SYNAPSE_API}/control/pause`, {
        method: 'POST',
        headers: { 'X-API-Key': 'synapse-dev' }
      });

      // Wait 2s then resume
      setTimeout(async () => {
        await fetch(`${SYNAPSE_API}/control/resume`, {
          method: 'POST',
          headers: { 'X-API-Key': 'synapse-dev' }
        });
        addNotification({ type: 'system', title: 'Synapse Restart', message: 'Pipeline restarted successfully', priority: 'high' });
        setIsRestarting(false);
      }, 2000);
    } catch (err) {
      addNotification({ type: 'error', title: 'Restart Failed', message: 'Check Synapse logs', priority: 'high' });
      setIsRestarting(false);
    }
  };

  const handleShutdown = async () => {
    const confirmed = window.confirm('SHUTDOWN Synapse? All processing will stop!');
    if (!confirmed) return;

    try {
      await fetch(`${SYNAPSE_API}/control/pause`, {
        method: 'POST',
        headers: { 'X-API-Key': 'synapse-dev' }
      });
      addNotification({ type: 'warning', title: 'Synapse Paused', message: 'No new tasks will process' });
    } catch (err) {
      addNotification({ type: 'error', title: 'Shutdown Failed', message: 'Could not pause Synapse', priority: 'high' });
    }
  };

  const isHealthy = health?.status === 'healthy';
  const recentNotifications = getRecent(10);

  return (
    <header className="hidden md:flex fixed top-[10px] left-0 right-0 z-[9999] px-2 sm:px-6 py-2 sm:py-3 items-center justify-between header-rgb-glow">
      {/* RGB animated border bottom */}
      <div className="absolute inset-0 bg-[#0d1117]/95 backdrop-blur-xl" />
      <div className="absolute bottom-0 left-0 right-0 h-[2px] header-rgb-border" />

      <div className="relative flex items-center gap-2 sm:gap-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <a href="/" className="text-lg sm:text-2xl font-black header-title-glow hover:scale-105 transition-transform">
            MOTHERSHIP
          </a>
          <span className="hidden sm:inline text-xs font-mono px-2 py-0.5 rounded bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 text-cyan-400">
            ControlDeck
          </span>
        </div>
        <nav className="hidden md:flex items-center gap-4">
          <a href="https://promptwizard.bot" target="_blank" rel="noopener noreferrer"
             className="text-gray-400 hover:text-white text-sm transition-colors flex items-center gap-1">
            <span>PromptWizard</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </nav>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* Queue Card - RGB glow when active - hidden on mobile */}
        <div
          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
            queueCount > 0
              ? 'header-stat-active'
              : 'bg-[#21262d]/80 border-[#30363d] text-gray-500'
          }`}
          title="Implementation Queue"
        >
          <span className="text-xs font-medium">Q</span>
          <span className={`text-lg font-bold ${queueCount > 0 ? 'text-cyan-300' : 'text-gray-600'}`}>
            {queueCount}
          </span>
        </div>

        {/* Pending Review Card - orange glow when items need human review */}
        {pendingReviewCount > 0 && (
          <a
            href="/fix-review"
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all bg-orange-500/10 border-orange-500/50 hover:border-orange-400 hover:bg-orange-500/20"
            title={`${pendingReviewCount} QA items need human review`}
          >
            <ClipboardCheck className="w-4 h-4 text-orange-400" />
            <span className="text-lg font-bold text-orange-300">{pendingReviewCount}</span>
            <span className="text-xs text-orange-400/80 hidden sm:inline">review</span>
          </a>
        )}

        {/* Synapse Status - pulsing RGB when online - compact on mobile */}
        <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border ${isHealthy ? 'header-status-online' : 'bg-[#21262d]/80 border-red-500/50'}`}>
          <div className={`w-2 h-2 rounded-full ${isHealthy ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
          <span className={`hidden sm:inline text-sm font-medium ${isHealthy ? 'text-green-400' : 'text-red-400'}`}>{isHealthy ? 'ONLINE' : 'OFFLINE'}</span>
        </div>

        {/* Processed Count - hidden on mobile */}
        <div className="hidden sm:block text-gray-400 text-sm px-3 py-1.5 rounded-lg bg-[#21262d]/80 border border-[#30363d] backdrop-blur">
          <span className="text-purple-400 font-bold">{workerCount}</span>
          <span className="text-gray-500 ml-1">done</span>
        </div>

        {/* Emergency Controls - hover glow - hidden on mobile */}
        <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-[#21262d]/80 border border-[#30363d] backdrop-blur">
          <button
            onClick={handleEmergencyRestart}
            disabled={isRestarting}
            className={`p-1.5 rounded transition-all ${
              isRestarting
                ? 'text-yellow-400 animate-spin'
                : 'text-orange-400 hover:bg-orange-500/20'
            }`}
            title="Restart Synapse"
          >
            <Power className="w-4 h-4" />
          </button>
          <button
            onClick={handleShutdown}
            className="p-1.5 rounded text-red-400 hover:bg-red-500/20 transition-all"
            title="Emergency Stop"
          >
            <AlertTriangle className="w-4 h-4" />
          </button>
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={`p-2 rounded-lg transition-all ${
              showNotifications ? 'bg-purple-500/20 text-purple-400' : 'text-gray-400 hover:text-white hover:bg-[#21262d]'
            }`}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notification Panel */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-[#161b22] border border-[#30363d] rounded-lg shadow-2xl shadow-black/50 overflow-hidden z-[100]">
              <div className="px-3 py-2 border-b border-[#30363d] flex justify-between items-center">
                <span className="text-sm font-medium text-white">Notifications</span>
                {recentNotifications.length > 0 && (
                  <button
                    onClick={dismissAll}
                    className="text-xs text-gray-500 hover:text-white"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto">
                {recentNotifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No notifications
                  </div>
                ) : (
                  recentNotifications.map(n => {
                    const style = NOTIFICATION_STYLES[n.type] || NOTIFICATION_STYLES.info;
                    return (
                      <div
                        key={n.id}
                        className={`px-3 py-2 border-b border-[#30363d]/50 flex items-start gap-2 ${style.bgColor} ${!n.read ? 'border-l-2 border-l-cyan-500' : ''}`}
                        onClick={() => markAsRead(n.id)}
                      >
                        <span className="text-sm flex-shrink-0 mt-0.5">{style.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${style.color}`}>{n.title}</p>
                          <p className="text-xs text-gray-400 truncate">{n.message}</p>
                          <p className="text-[10px] text-gray-500">{new Date(n.timestamp).toLocaleTimeString()}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                          className="text-gray-500 hover:text-white flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              {/* View All Link */}
              <a
                href="/notifications"
                className="block px-3 py-2 text-center text-xs text-cyan-400 hover:bg-[#21262d] border-t border-[#30363d] transition-colors"
              >
                View all notifications
              </a>
            </div>
          )}
        </div>

        {onToggleAdmin && (
          <button
            onClick={onToggleAdmin}
            className="text-gray-500 hover:text-cyan-400 transition-colors text-xs opacity-50 hover:opacity-100"
            title={isAdmin ? 'Switch to Dashboard view' : 'Switch to Admin view'}
          >
            {isAdmin ? '👁️' : '⚙️'}
          </button>
        )}
        {userMenu}
      </div>
    </header>
  );
};

export default Header;
